/**
 * 임금 저장/로드 매핑 — DB 컬럼명(cwhd_*) 그대로 사용
 */
import type { CommonWageListRow } from '@/types/commonWage';
import type { CommonWageSaveMeta } from '@/constants/commonWageFields';
import { CWHD_FORM_INPUT_FIELDS, COMMON_WAGE_DEFAULT_WORK_HOURS } from '@/constants/commonWageFields';
import {
  applyWageOnesTruncateToCalcResult,
  type CommonWageCalcResult,
  type CommonWageFormNumbers,
  type CommonWageManualFlags,
  type CommonWageSettings,
} from '@/composables/wage/useCommonWageCalc';
import { parseWageYmParts } from '@/policies/wageOnesTruncatePolicy';
import { formatWageNumber, parseHours, parseWageNumber, formatHoursDisplay } from '@/composables/wage/useWageCalculator';

export type CommonWageFormStrings = Record<(typeof CWHD_FORM_INPUT_FIELDS)[number], string>;

export function createEmptyFormStrings(): CommonWageFormStrings {
  const f = {} as CommonWageFormStrings;
  for (const key of CWHD_FORM_INPUT_FIELDS) {
    f[key] = '';
  }
  return f;
}

/** 근무일수 미입력 시 근무시간÷8로 추정 (일 8시간 기준, 소수 1자리) */
export function inferWorkDaysFromHours(workHours: number | null | undefined): number | null {
  const h = Number(workHours);
  if (!Number.isFinite(h) || h <= 0) return null;
  const days = Math.round((h / 8) * 10) / 10;
  return Math.min(days, 31);
}

/**
 * DB/행에서 표시할 근무일수.
 * 저장된 일수가 있으면 그 값.
 * defaultWorkDays 키가 있으면 비어 있을 때 그 값(달력 영업일)을 쓰고, 아직 없으면 비워 둔다.
 * 키가 없으면 근무시간÷8로 추정한다.
 */
export function resolveWorkDaysForDisplay(
  workDays: number | null | undefined,
  workHours: number | null | undefined,
  options?: { defaultWorkDays?: number | null }
): number | null {
  const days = Number(workDays);
  if (Number.isFinite(days) && days > 0) return days;
  if (options && Object.prototype.hasOwnProperty.call(options, 'defaultWorkDays')) {
    const fallback = Number(options.defaultWorkDays);
    if (Number.isFinite(fallback) && fallback > 0) return Math.floor(fallback);
    return null;
  }
  return inferWorkDaysFromHours(workHours);
}

/** DB/행에 근무정보(시급·근무시간 등)가 저장되어 있는지 */
export function rowHasSavedWorkInfo(row: CommonWageListRow): boolean {
  if (Number(row.cwhd_pay_calc_hourly) > 0) return true;
  if (Number(row.cwhd_work_hours) > 0) return true;
  if (Number(row.cwhd_work_days) > 0) return true;
  if (Number(row.cwhd_basic) > 0) return true;
  return false;
}

/** rowToFormStrings — UI 기본값(209h·최저시급) 적용 여부 */
export function getRowToFormDefaultOptions(row: CommonWageListRow) {
  const skip = !rowHasSavedWorkInfo(row);
  return { skipWorkHoursDefault: skip, skipHourlyDefault: skip };
}

/** 목록/행 → 모달 입력 문자열 */
export function rowToFormStrings(
  row: CommonWageListRow,
  minHourlyWage?: number | null,
  options?: {
    skipWorkHoursDefault?: boolean;
    skipHourlyDefault?: boolean;
    /** 미저장 근무일수. null이면 공휴일 조회 전이라 비워 둔다 */
    defaultWorkDays?: number | null;
  }
): CommonWageFormStrings {
  const f = createEmptyFormStrings();

  const setAmt = (key: keyof CommonWageFormStrings, val: number | null | undefined) => {
    if (val != null && Number(val) !== 0) {
      f[key] = formatWageNumber(val);
    }
  };
  const setHr = (key: keyof CommonWageFormStrings, val: number | null | undefined) => {
    if (val != null && Number(val) !== 0) {
      f[key] = formatHoursDisplay(val);
    }
  };
  const setDays = (key: keyof CommonWageFormStrings, val: number | null | undefined) => {
    if (val != null && Number(val) !== 0) {
      f[key] = String(Math.floor(Number(val)));
    }
  };

  setAmt('cwhd_pay_calc_hourly', row.cwhd_pay_calc_hourly);
  setDays(
    'cwhd_work_days',
    resolveWorkDaysForDisplay(row.cwhd_work_days, row.cwhd_work_hours, options)
  );
  setHr('cwhd_work_hours', row.cwhd_work_hours);
  setHr('cwhd_overtime_hours', row.cwhd_overtime_hours);
  setHr('cwhd_night_hours', row.cwhd_night_hours);
  setHr('cwhd_holiday_hours', row.cwhd_holiday_hours);
  setAmt('cwhd_dayoff', row.cwhd_dayoff);
  setAmt('cwhd_senior', row.cwhd_senior);
  setAmt('cwhd_sunim', row.cwhd_sunim);
  setAmt('cwhd_bonus', row.cwhd_bonus);
  setAmt('cwhd_bosu', row.cwhd_bosu);
  setAmt('cwhd_bojon', row.cwhd_bojon);
  setAmt('cwhd_other', row.cwhd_other);
  setAmt('cwhd_dang', row.cwhd_dang);
  setAmt('cwhd_lunch', row.cwhd_lunch);
  setAmt('cwhd_car', row.cwhd_car);
  setAmt('cwhd_child', row.cwhd_child);
  if (row.cwhd_memo) f.cwhd_memo = row.cwhd_memo;

  // 시급 컬럼 없이 기본급만 저장된 DB 행 — 자동계산 시 0 방지
  if (!f.cwhd_pay_calc_hourly) {
    const basic = Number(row.cwhd_basic);
    const hours = parseHours(row.cwhd_work_hours);
    if (basic > 0 && hours > 0) {
      f.cwhd_pay_calc_hourly = formatWageNumber(Math.round(basic / hours));
    }
  }

  return applyCommonWageFormDefaults(f, minHourlyWage, options);
}

/** 시급·근무시간 미입력 시 기본값 채움 (최저시급, 209시간) — 저장된 근무정보가 있을 때만 */
export function applyCommonWageFormDefaults(
  form: CommonWageFormStrings,
  minHourlyWage?: number | null,
  options?: { skipWorkHoursDefault?: boolean; skipHourlyDefault?: boolean }
): CommonWageFormStrings {
  const f = { ...form };
  if (!options?.skipWorkHoursDefault && !parseHours(f.cwhd_work_hours)) {
    f.cwhd_work_hours = formatHoursDisplay(COMMON_WAGE_DEFAULT_WORK_HOURS);
  }
  if (!options?.skipHourlyDefault && !parseWageNumber(f.cwhd_pay_calc_hourly) && minHourlyWage) {
    f.cwhd_pay_calc_hourly = formatWageNumber(minHourlyWage);
  }
  return f;
}

/** 모달 문자열 → 계산용 숫자 (키 = cwhd_*) */
export function formStringsToNumbers(form: CommonWageFormStrings): CommonWageFormNumbers {
  return {
    cwhd_pay_calc_hourly: parseWageNumber(form.cwhd_pay_calc_hourly),
    cwhd_work_days: parseInt(String(form.cwhd_work_days).replace(/[^\d]/g, ''), 10) || 0,
    cwhd_work_hours: parseHours(form.cwhd_work_hours),
    cwhd_overtime_hours: parseHours(form.cwhd_overtime_hours),
    cwhd_night_hours: parseHours(form.cwhd_night_hours),
    cwhd_holiday_hours: parseHours(form.cwhd_holiday_hours),
    cwhd_dayoff: parseWageNumber(form.cwhd_dayoff),
    cwhd_senior: parseWageNumber(form.cwhd_senior),
    cwhd_sunim: parseWageNumber(form.cwhd_sunim),
    cwhd_bonus: parseWageNumber(form.cwhd_bonus),
    cwhd_bosu: parseWageNumber(form.cwhd_bosu),
    cwhd_bojon: parseWageNumber(form.cwhd_bojon),
    cwhd_other: parseWageNumber(form.cwhd_other),
    cwhd_dang: parseWageNumber(form.cwhd_dang),
    cwhd_lunch: parseWageNumber(form.cwhd_lunch),
    cwhd_car: parseWageNumber(form.cwhd_car),
    cwhd_child: parseWageNumber(form.cwhd_child),
  };
}

export type CommonWageDeductNumbers = Record<
  | 'cwhd_l_yeon'
  | 'cwhd_l_gun'
  | 'cwhd_l_jang'
  | 'cwhd_l_go'
  | 'cwhd_l_so'
  | 'cwhd_l_ji'
  | 'cwhd_l_jiwon'
  | 'cwhd_l_sik'
  | 'cwhd_l_etc'
  | 'cwhd_l_yearend'
  | 'cwhd_c_yeon'
  | 'cwhd_c_gun'
  | 'cwhd_c_jang'
  | 'cwhd_c_go'
  | 'cwhd_c_sil'
  | 'cwhd_c_san'
  | 'cwhd_c_out',
  number
>;

export function emptyDeductNumbers(): CommonWageDeductNumbers {
  return {
    cwhd_l_yeon: 0,
    cwhd_l_gun: 0,
    cwhd_l_jang: 0,
    cwhd_l_go: 0,
    cwhd_l_so: 0,
    cwhd_l_ji: 0,
    cwhd_l_jiwon: 0,
    cwhd_l_sik: 0,
    cwhd_l_etc: 0,
    cwhd_l_yearend: 0,
    cwhd_c_yeon: 0,
    cwhd_c_gun: 0,
    cwhd_c_jang: 0,
    cwhd_c_go: 0,
    cwhd_c_sil: 0,
    cwhd_c_san: 0,
    cwhd_c_out: 0,
  };
}

export function rowToDeductNumbers(row: CommonWageListRow): CommonWageDeductNumbers {
  return {
    cwhd_l_yeon: Number(row.cwhd_l_yeon) || 0,
    cwhd_l_gun: Number(row.cwhd_l_gun) || 0,
    cwhd_l_jang: Number(row.cwhd_l_jang) || 0,
    cwhd_l_go: Number(row.cwhd_l_go) || 0,
    cwhd_l_so: Number(row.cwhd_l_so) || 0,
    cwhd_l_ji: Number(row.cwhd_l_ji) || 0,
    cwhd_l_jiwon: Number(row.cwhd_l_jiwon) || 0,
    cwhd_l_sik: Number(row.cwhd_l_sik) || 0,
    cwhd_l_etc: Number(row.cwhd_l_etc) || 0,
    cwhd_l_yearend: Number(row.cwhd_l_yearend) || 0,
    cwhd_c_yeon: Number(row.cwhd_c_yeon) || 0,
    cwhd_c_gun: Number(row.cwhd_c_gun) || 0,
    cwhd_c_jang: Number(row.cwhd_c_jang) || 0,
    cwhd_c_go: Number(row.cwhd_c_go) || 0,
    cwhd_c_sil: Number(row.cwhd_c_sil) || 0,
    cwhd_c_san: Number(row.cwhd_c_san) || 0,
    cwhd_c_out: Number(row.cwhd_c_out) || 0,
  };
}

export function calcToDeductNumbers(calc: CommonWageCalcResult): CommonWageDeductNumbers {
  return {
    cwhd_l_yeon: calc.cwhd_l_yeon,
    cwhd_l_gun: calc.cwhd_l_gun,
    cwhd_l_jang: calc.cwhd_l_jang,
    cwhd_l_go: calc.cwhd_l_go,
    cwhd_l_so: calc.cwhd_l_so,
    cwhd_l_ji: calc.cwhd_l_ji,
    cwhd_l_jiwon: calc.cwhd_l_jiwon,
    cwhd_l_sik: calc.cwhd_l_sik,
    cwhd_l_etc: calc.cwhd_l_etc,
    cwhd_l_yearend: calc.cwhd_l_yearend,
    cwhd_c_yeon: calc.cwhd_c_yeon,
    cwhd_c_gun: calc.cwhd_c_gun,
    cwhd_c_jang: calc.cwhd_c_jang,
    cwhd_c_go: calc.cwhd_c_go,
    cwhd_c_sil: calc.cwhd_c_sil,
    cwhd_c_san: calc.cwhd_c_san,
    cwhd_c_out: calc.cwhd_c_out,
  };
}

/** 계산 결과 → DB 저장 필드 (cwhd_* 키) */
export function calcToSaveFields(
  calc: CommonWageCalcResult,
  form: CommonWageFormStrings,
  settings: Pick<
    CommonWageSettings,
    'cwhd_production_nontax' | 'cwhd_lunch_expense' | 'cwhd_is_owner'
  >
): Record<string, number | string | null | Record<string, boolean>> {
  const production = settings.cwhd_production_nontax;
  return {
    cwhd_pay_calc_hourly: parseWageNumber(form.cwhd_pay_calc_hourly),
    cwhd_work_days: parseInt(String(form.cwhd_work_days).replace(/[^\d]/g, ''), 10) || 0,
    cwhd_work_hours: parseHours(form.cwhd_work_hours),
    cwhd_basic: calc.cwhd_basic,
    cwhd_overtime_hours: parseHours(form.cwhd_overtime_hours),
    cwhd_overtime: calc.cwhd_overtime,
    cwhd_overtime_nontax: production ? calc.cwhd_overtime_nontax : 0,
    cwhd_night_hours: parseHours(form.cwhd_night_hours),
    cwhd_night: calc.cwhd_night,
    cwhd_night_nontax: production ? calc.cwhd_night_nontax : 0,
    cwhd_holiday_hours: parseHours(form.cwhd_holiday_hours),
    cwhd_holiday: calc.cwhd_holiday,
    cwhd_holiday_nontax: production ? calc.cwhd_holiday_nontax : 0,
    cwhd_dayoff: parseWageNumber(form.cwhd_dayoff),
    cwhd_senior: parseWageNumber(form.cwhd_senior),
    cwhd_sunim: parseWageNumber(form.cwhd_sunim),
    cwhd_bonus: parseWageNumber(form.cwhd_bonus),
    cwhd_bosu: parseWageNumber(form.cwhd_bosu),
    cwhd_bojon: parseWageNumber(form.cwhd_bojon),
    cwhd_other: parseWageNumber(form.cwhd_other),
    cwhd_dang: parseWageNumber(form.cwhd_dang),
    cwhd_lunch: parseWageNumber(form.cwhd_lunch),
    cwhd_car: parseWageNumber(form.cwhd_car),
    cwhd_child: parseWageNumber(form.cwhd_child),
    cwhd_wage_total: calc.cwhd_wage_total,
    cwhd_taxable: calc.cwhd_taxable,
    cwhd_usual: calc.cwhd_usual,
    cwhd_l_yeon: calc.cwhd_l_yeon,
    cwhd_l_gun: calc.cwhd_l_gun,
    cwhd_l_jang: calc.cwhd_l_jang,
    cwhd_l_go: calc.cwhd_l_go,
    cwhd_l_so: calc.cwhd_l_so,
    cwhd_l_ji: calc.cwhd_l_ji,
    cwhd_l_jiwon: calc.cwhd_l_jiwon,
    cwhd_l_sik: calc.cwhd_l_sik,
    cwhd_l_etc: calc.cwhd_l_etc,
    cwhd_l_yearend: calc.cwhd_l_yearend,
    cwhd_l_total: calc.cwhd_l_total,
    cwhd_c_yeon: calc.cwhd_c_yeon,
    cwhd_c_gun: calc.cwhd_c_gun,
    cwhd_c_jang: calc.cwhd_c_jang,
    cwhd_c_go: calc.cwhd_c_go,
    cwhd_c_sil: calc.cwhd_c_sil,
    cwhd_c_san: calc.cwhd_c_san,
    cwhd_c_out: calc.cwhd_c_out,
    cwhd_c_total: calc.cwhd_c_total,
    cwhd_manual: 1,
    cwhd_memo: form.cwhd_memo || null,
    cwhd_production_nontax: settings.cwhd_production_nontax ? 1 : 0,
    cwhd_lunch_expense: settings.cwhd_lunch_expense ? 1 : 0,
    cwhd_is_owner: settings.cwhd_is_owner ? 1 : 0,
  };
}

export type CommonWageSavePayload = CommonWageSaveMeta &
  Record<string, number | string | null | CommonWageManualFlags | undefined> & {
    cwhd_manual_flags?: CommonWageManualFlags | null;
  };

export function buildSavePayload(params: {
  meta: CommonWageSaveMeta;
  calc: CommonWageCalcResult;
  form: CommonWageFormStrings;
  settings: CommonWageSettings;
  manualFlags: CommonWageManualFlags;
  basicCalcMode?: string | null;
}): CommonWageSavePayload {
  const { meta, calc, form, settings, manualFlags, basicCalcMode } = params;
  const ym = parseWageYmParts(meta.cwhd_ym);
  const calcForSave = ym
    ? applyWageOnesTruncateToCalcResult(calc, ym.year, ym.month)
    : calc;
  return {
    ...meta,
    ...calcToSaveFields(calcForSave, form, settings),
    cwhd_basic_calc_mode: basicCalcMode === 'basic' ? 'basic' : 'hourly',
    cwhd_manual_flags: { ...manualFlags },
  };
}

/** DB 저장 행 → 화면 표시용 계산 스냅샷 (자동 재계산 없이 DB 값 그대로) */
export function rowToCalcSnapshot(row: CommonWageListRow): CommonWageCalcResult {
  const n = (v: number | null | undefined) => Number(v) || 0;
  const wageTotal = n(row.cwhd_wage_total);
  const lTotal = n(row.cwhd_l_total);
  const cwhd_overtime = n(row.cwhd_overtime);
  const cwhd_night = n(row.cwhd_night);
  const cwhd_holiday = n(row.cwhd_holiday);
  const cwhd_overtime_nontax = n(row.cwhd_overtime_nontax);
  const cwhd_night_nontax = n(row.cwhd_night_nontax);
  const cwhd_holiday_nontax = n(row.cwhd_holiday_nontax);
  return {
    cwhd_basic: n(row.cwhd_basic),
    overtimeTotal: cwhd_overtime + cwhd_overtime_nontax,
    nightTotal: cwhd_night + cwhd_night_nontax,
    holidayTotal: cwhd_holiday + cwhd_holiday_nontax,
    cwhd_overtime,
    cwhd_night,
    cwhd_holiday,
    cwhd_overtime_nontax,
    cwhd_night_nontax,
    cwhd_holiday_nontax,
    cwhd_wage_total: wageTotal,
    cwhd_taxable: n(row.cwhd_taxable),
    cwhd_usual: n(row.cwhd_usual),
    cwhd_l_yeon: n(row.cwhd_l_yeon),
    cwhd_l_gun: n(row.cwhd_l_gun),
    cwhd_l_jang: n(row.cwhd_l_jang),
    cwhd_l_go: n(row.cwhd_l_go),
    cwhd_l_so: n(row.cwhd_l_so),
    cwhd_l_ji: n(row.cwhd_l_ji),
    cwhd_l_jiwon: n(row.cwhd_l_jiwon),
    cwhd_l_sik: n(row.cwhd_l_sik),
    cwhd_l_etc: n(row.cwhd_l_etc),
    cwhd_l_yearend: n(row.cwhd_l_yearend),
    cwhd_l_total: lTotal,
    cwhd_c_yeon: n(row.cwhd_c_yeon),
    cwhd_c_gun: n(row.cwhd_c_gun),
    cwhd_c_jang: n(row.cwhd_c_jang),
    cwhd_c_go: n(row.cwhd_c_go),
    cwhd_c_sil: n(row.cwhd_c_sil),
    cwhd_c_san: n(row.cwhd_c_san),
    cwhd_c_out: n(row.cwhd_c_out),
    cwhd_c_total: n(row.cwhd_c_total),
    netPay: wageTotal - lTotal,
  };
}

export function rowToSettings(row: CommonWageListRow): Pick<
  CommonWageSettings,
  'cwhd_production_nontax' | 'cwhd_lunch_expense' | 'cwhd_is_owner'
> {
  return {
    cwhd_production_nontax: row.cwhd_production_nontax === 1,
    cwhd_lunch_expense: row.cwhd_lunch_expense === 1,
    cwhd_is_owner: row.cwhd_is_owner === 1,
  };
}

/** cwhd_manual_flags JSON 파싱 */
export function parseManualFlagsFromRow(raw: unknown): CommonWageManualFlags {
  if (raw == null || raw === '') return {};
  let parsed: unknown = raw;
  // MySQL JSON/TEXT · 이중 인코딩 대응
  for (let i = 0; i < 2 && typeof parsed === 'string'; i++) {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const out = { ...(parsed as CommonWageManualFlags) };
  // 레거시 'go' 플래그를 l_go, c_go로 변환
  if (isCommonWageManualFlagOn(out, 'go') && !isCommonWageManualFlagOn(out, 'l_go') && !isCommonWageManualFlagOn(out, 'c_go')) {
    out.l_go = true;
    out.c_go = true;
    delete out.go;
  }
  return out;
}

/** 수동고정 체크 — true/1/'Y'/'true' 등 */
export function isCommonWageManualFlagOn(
  flags: CommonWageManualFlags | null | undefined,
  key: keyof CommonWageManualFlags
): boolean {
  if (!flags) return false;
  const v = flags[key] as unknown;
  return v === true || v === 1 || v === '1' || v === 'Y' || v === 'y' || v === 'true';
}
